import React, { useRef, useEffect, useState } from 'react'
import styled from 'styled-components'
import { RADOM_COLORS } from '../util/Constants'

interface IProps {
  selectedTab: string
  tabs: string[]
  onTabChange: (tab: string) => void
  rightContent?: React.ReactNode
}

const TabsContainer = styled.div`
  position: relative;
  border-bottom: 1px solid ${RADOM_COLORS.GRAY9};
  display: flex;
  gap: 10px;
  font-size: 14px;
`

const Tab = styled.div<{ $selected?: boolean }>`
  cursor: pointer;
  padding: 10px 20px;
  position: relative;
  z-index: 1;
  transition: 0.2s ease all;
  user-select: none;
  font-weight: 400;
  ${({ $selected }) => $selected && `color: ${RADOM_COLORS.RADOM_BLUE};`}
  ${({ $selected }) => !$selected && 'opacity: 0.5;'}

  &:hover {
    opacity: 1;
    color: ${RADOM_COLORS.RADOM_BLUE};
  }
`

const AnimatedIndicator = styled.div<{ $left: number, $width: number }>`
  position: absolute;
  bottom: -1px;
  left: ${({ $left }) => $left}px;
  width: ${({ $width }) => $width}px;
  height: 2px;
  background: ${RADOM_COLORS.RADOM_BLUE};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 2;
`

const AnimatedBackground = styled.div<{ $left: number, $width: number }>`
  position: absolute;
  top: 0;
  left: ${({ $left }) => $left}px;
  width: ${({ $width }) => $width}px;
  height: 100%;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 0;
`

const AnimatedTabs = (props: IProps): React.ReactElement => {
  const tabRefs = useRef<{ [key: string]: HTMLDivElement | null }>({})
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 })

  useEffect(() => {
    const selectedTabElement = tabRefs.current[props.selectedTab]
    if (selectedTabElement) {
      setIndicatorStyle({
        left: selectedTabElement.offsetLeft,
        width: selectedTabElement.offsetWidth
      })
    }
  }, [props.selectedTab, props.tabs])

  return (
    <TabsContainer>
      <AnimatedBackground $left={indicatorStyle.left} $width={indicatorStyle.width} />
      {props.tabs.map(tab => (
        <Tab
          key={tab}
          ref={(el) => (tabRefs.current[tab] = el)}
          $selected={tab === props.selectedTab}
          onClick={() => props.onTabChange(tab)}
        >
          {tab}
        </Tab>
      ))}
      {props.rightContent && (
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
          {props.rightContent}
        </div>
      )}
      <AnimatedIndicator $left={indicatorStyle.left} $width={indicatorStyle.width} />
    </TabsContainer>
  )
}

export default AnimatedTabs
